import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../utils/axios';
import { MdEdit, MdDelete, MdAdd, MdClose } from 'react-icons/md';
import './Categories.css';

const GROUP_ORDER = ['income', 'fixed', 'flexible', 'non_monthly'];
const GROUP_LABELS = {
  income: 'Income',
  fixed: 'Fixed Expenses',
  flexible: 'Flexible Expenses',
  non_monthly: 'Non-Monthly Expenses',
};

const groupOptions = [
  { value: 'income', label: 'Income' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'flexible', label: 'Flexible' },
  { value: 'non_monthly', label: 'Non-Monthly' },
];

const COMMON_EMOJIS = [
  '🍔', '🍕', '🍜', '☕', '🍱',
  '🚗', '🚕', '✈️', '🚲', '🚌',
  '🛍️', '👔', '👗', '👟', '💄',
  '🎬', '🎮', '🎵', '📺', '🎨',
  '📄', '💡', '💧', '📱', '🏠',
  '🏥', '💊', '⚕️', '🩺', '💉',
  '🏖️', '🗺️', '🎫', '🏨', '🧳',
  '📚', '📝', '🎓', '✏️', '💻',
  '💇', '💅', '🏋️', '🧘', '💆',
  '💰', '💵', '💸', '🏦', '💳',
  '📌', '🔧', '🎯', '⭐', '❤️',
];

const bucketOf = (cat) => {
  if (cat.type === 'income') return 'income';
  const g = (cat.budget_group || 'flexible').toLowerCase();
  if (g === 'fixed') return 'fixed';
  if (g === 'non_monthly') return 'non_monthly';
  return 'flexible';
};

const EditorModal = ({ category, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: category?.name || '',
    icon: category?.icon || '',
    color: category?.color || '#3B82F6',
    description: category?.description || '',
    budget_group: bucketOf(category || {}),
  });
  const [showEmoji, setShowEmoji] = useState(false);
  const emojiRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
    };
    if (showEmoji) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showEmoji]);

  const isDefault = category && category.user_id === null;
  const isIncome = category && category.type === 'income';
  const canEditFields = !isDefault && !isIncome;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card categories-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><MdClose /></button>
        <h3 className="modal-title">{category ? 'Edit category' : 'New category'}</h3>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(form);
          }}
        >
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={!canEditFields && category}
                required
              />
            </div>
            <div className="form-group" style={{ flex: '0 0 100px' }}>
              <label>Icon</label>
              <div className="cat-icon-wrap" ref={emojiRef}>
                <button
                  type="button"
                  className="cat-icon-btn"
                  onClick={() => setShowEmoji(!showEmoji)}
                  disabled={!canEditFields && category}
                >
                  {form.icon || '😀'}
                </button>
                {showEmoji && (
                  <div className="emoji-picker-dropdown">
                    {COMMON_EMOJIS.map((emo, i) => (
                      <button
                        key={i}
                        type="button"
                        className="emoji-option"
                        onClick={() => {
                          setForm({ ...form, icon: emo });
                          setShowEmoji(false);
                        }}
                      >
                        {emo}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group" style={{ flex: '0 0 80px' }}>
              <label>Color</label>
              <input
                type="color"
                className="input color-input"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                disabled={!canEditFields && category}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Budget group</label>
            <select
              className="input"
              value={form.budget_group}
              onChange={(e) => setForm({ ...form, budget_group: e.target.value })}
              disabled={isIncome}
            >
              {groupOptions.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
            <div className="form-hint">Used to bucket this category in the Budget and Dashboard panels.</div>
          </div>

          <div className="form-group">
            <label>Description (optional)</label>
            <textarea
              className="input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows="2"
              disabled={!canEditFields && category}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="button button-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="button">{category ? 'Save' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // category or {} for new
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/api/categories');
      setCategories(res.data);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to load categories' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const grouped = useMemo(() => {
    const map = { income: [], fixed: [], flexible: [], non_monthly: [] };
    categories.forEach((c) => map[bucketOf(c)].push(c));
    return map;
  }, [categories]);

  const handleSave = async (form) => {
    try {
      if (editing && editing.id) {
        await api.put(`/api/categories/${editing.id}`, form);
        setMessage({ type: 'success', text: 'Category updated' });
      } else {
        await api.post('/api/categories', { ...form, type: form.budget_group === 'income' ? 'income' : 'expense' });
        setMessage({ type: 'success', text: 'Category created' });
      }
      setEditing(null);
      fetchCategories();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Failed to save category' });
    }
  };

  const handleInlineGroupChange = async (category, newGroup) => {
    try {
      await api.put(`/api/categories/${category.id}`, { budget_group: newGroup });
      fetchCategories();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Failed to update' });
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"? Transactions in this category will be uncategorized.`)) return;
    try {
      await api.delete(`/api/categories/${category.id}`);
      setMessage({ type: 'success', text: 'Category deleted' });
      fetchCategories();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Failed to delete' });
    }
  };

  if (loading) return <div className="loading">Loading categories...</div>;

  return (
    <div className="cat-page">
      {message.text && <div className={message.type}>{message.text}</div>}

      <div className="cat-header">
        <span className="cat-header-sub">
          {categories.length} categor{categories.length === 1 ? 'y' : 'ies'} · grouped by budget bucket
        </span>
        <button className="button" onClick={() => setEditing({})}>
          <MdAdd /> Add category
        </button>
      </div>

      {GROUP_ORDER.map((key) => {
        const cats = grouped[key];
        if (cats.length === 0) return null;
        return (
          <div key={key} className="card cat-section">
            <div className="cat-section-head">
              <span className="cat-section-title">{GROUP_LABELS[key]}</span>
              <span className="cat-section-count">{cats.length}</span>
            </div>
            <div className="cat-list">
              {cats.map((c) => {
                const isDefault = c.user_id === null;
                const isIncome = c.type === 'income';
                return (
                  <div key={c.id} className="cat-row">
                    <div className="cat-row-left">
                      <div
                        className="cat-row-icon"
                        style={{ background: (c.color || '#9a9a96') + '22', color: c.color || '#6b6b6b' }}
                      >
                        {c.icon || '•'}
                      </div>
                      <div className="cat-row-name">
                        {c.name}
                        {isDefault && <span className="cat-badge">Default</span>}
                        {isIncome && <span className="cat-badge income">Income</span>}
                      </div>
                    </div>
                    <div className="cat-row-right">
                      <select
                        className="input cat-group-select"
                        value={bucketOf(c)}
                        disabled={isIncome}
                        onChange={(e) => handleInlineGroupChange(c, e.target.value)}
                      >
                        {groupOptions.map((g) => (
                          <option key={g.value} value={g.value}>{g.label}</option>
                        ))}
                      </select>
                      <button
                        className="icon-button"
                        onClick={() => setEditing(c)}
                        title="Edit"
                      >
                        <MdEdit />
                      </button>
                      <button
                        className="icon-button danger"
                        onClick={() => handleDelete(c)}
                        disabled={isDefault || isIncome}
                        title={isDefault || isIncome ? 'Built-in category, cannot delete' : 'Delete'}
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {editing !== null && (
        <EditorModal
          category={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default Categories;
